import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IContest} from "data/stores/contests/contests.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import {useNavigate} from "react-router";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import {WinnerCount} from "data/enums";
import type {
	IContestPlayersStore,
	IContestPlayer,
} from "data/stores/contest_players/contest_players.store";

interface IProps {
	contest: IContest;
	navigate: ReturnType<typeof useNavigate>;
}
export interface IContestCardController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get isLoading(): boolean;
	get awaySquad(): ISquad | undefined;
	get homeSquad(): ISquad | undefined;
	get homeSquadResults(): string | undefined;
	get awaySquadResults(): string | undefined;
	get isVoted(): boolean;
	get voteCopy(): string;
	get winnerPlayer(): IContestPlayer | null | undefined;
	get opponentWinnerPlayer(): IContestPlayer | null | undefined;
	get winnersCount(): WinnerCount;

	onSelectCard: () => void;
}

@injectable()
export class ContestCardController implements IContestCardController {
	@observable private _isLoading: boolean = false;
	@observable private _contest: IContest | null = null;
	@observable private _navigate!: ReturnType<typeof useNavigate>;

	get isLoading() {
		return this._isLoading;
	}

	get awaySquad() {
		if (!this._contest) return;

		return this._squadsStore.getSquadById(this._contest.awaySquadId);
	}

	get homeSquad() {
		if (!this._contest) return;

		return this._squadsStore.getSquadById(this._contest.homeSquadId);
	}

	get homeSquadResults() {
		if (!this.homeSquad) return "";

		return `${this.homeSquad?.wins}-${this.homeSquad?.losses}`;
	}

	get awaySquadResults() {
		if (!this.awaySquad) return "";

		return `${this.awaySquad?.wins}-${this.awaySquad?.losses}`;
	}

	get isVoted() {
		const prediction = this._predictionsStore.getPredictionByContestId(this._contest?.id ?? 1);

		return Boolean(prediction?.playerId);
	}

	get voteCopy() {
		const prediction = this._predictionsStore.getPredictionByContestId(this._contest?.id ?? 1);

		if (prediction?.playerId) {
			return this.i18n.t("game.prediction.status.voted", "Voted");
		}

		return this.i18n.t("game.prediction.status.vote_now", "Vote now");
	}

	get winnerPlayer() {
		if (this._contest?.winnerId) {
			return this._contestPlayersStore.getPlayerById(this._contest.winnerId);
		}

		return null;
	}

	get opponentWinnerPlayer() {
		if (this._contest?.opponentWinnerId) {
			return this._contestPlayersStore.getPlayerById(this._contest.opponentWinnerId);
		}

		return null;
	}

	get winnersCount() {
		if (this.winnerPlayer && this.opponentWinnerPlayer) {
			if (this.winnerPlayer.playerId === this.opponentWinnerPlayer.playerId) {
				return WinnerCount.EQUAL;
			} else {
				return WinnerCount.DOUBLE;
			}
		}

		return WinnerCount.SINGLE;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.ContestPlayersStore) private _contestPlayersStore: IContestPlayersStore
	) {
		makeAutoObservable(this);
	}

	onSelectCard = () => {
		this._navigate(`/contest/${this._contest?.gameFeedId ?? 1}`);
	};

	init({contest, navigate}: IProps) {
		this._contest = contest;
		this._navigate = navigate;
		this._isLoading = true;

		setTimeout(() => {
			this._isLoading = false;
		}, 850);
	}
}
